import React, { ChangeEvent } from 'react';
import { IssueBox } from '../Styles/IssueTool';
import { WithTranslation, withTranslation } from 'react-i18next';
import { get } from 'lodash';
import TextareaAutosize from 'react-textarea-autosize';
import Highlighter from 'web-highlighter';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

type IssueToolProps = WithTranslation & {
  addIssue: (message: string, selected: string) => Promise<void>;
  onClose: () => void;
  showIssueBox: boolean;
  materialContainerRef: React.RefObject<HTMLDivElement>;
};

type IssueToolState = {
  message: string;
  selection: string;
  emptyMessage: boolean;
};

class IssueTool extends React.PureComponent<IssueToolProps, IssueToolState> {
  state = {
    message: '',
    selection: '',
    emptyMessage: false,
  };

  private issueToolRef: React.RefObject<HTMLDivElement> = React.createRef();

  private _highlighter = new Highlighter({
    style: {
      className: 'issue-selection-highlighter',
    },
  });

  componentDidMount(): void {
    this._highlighter.on(Highlighter.event.CREATE, (data: unknown) => {
      const text = get(data, 'sources[0].text');
      if (text) {
        this.setState({ selection: text });
        this._highlighter.stop();
      }
    });

    this._highlighter.on(Highlighter.event.CLICK, () => {
      this._highlighter.removeAll();
      this._highlighter.run();
      this.setState({ selection: '' });
    });

    this._highlighter.run();

    document.addEventListener('mousedown', this.handleClickOutside);
  }

  componentWillUnmount(): void {
    document.removeEventListener('mousedown', this.handleClickOutside);
    this._highlighter.dispose();
  }

  handleClickOutside = (event: MouseEvent): void => {
    const { materialContainerRef } = this.props;
    if (
      materialContainerRef?.current &&
      event.target &&
      !materialContainerRef.current.contains(event.target as Node) &&
      !this.issueToolRef.current?.contains(event.target as Node)
    ) {
      this._highlighter.removeAll();
      this._highlighter.run();
      this.setState({ selection: '' });
    }
  };

  onChangeMessage = (event: ChangeEvent<HTMLTextAreaElement>): void => {
    const { value } = event.target;
    this.setState({ message: value, emptyMessage: value === '' });
  };

  onCloseHandler = (): void => {
    const { onClose } = this.props;
    this._highlighter.dispose();
    this.setState({ emptyMessage: false });
    onClose();
  };

  onAddIssue = (): void => {
    const { addIssue } = this.props;
    const { message, selection } = this.state;

    this.setState({ emptyMessage: message === '' });
    if (message === '') {
      return;
    }

    addIssue(message, selection);

    this.setState({ message: '', selection: '' });
    this.onCloseHandler();
  };

  removeSelection = (): void => {
    this.setState({ selection: '' });
    this._highlighter.removeAll();
    this._highlighter.run();
  };

  componentDidUpdate(): void {
    const { showIssueBox } = this.props;
    if (showIssueBox) {
      this._highlighter.run();
    } else {
      this._highlighter.dispose();
    }
  }

  render(): React.ReactElement {
    const { message, emptyMessage } = this.state;
    const { t } = this.props;

    return (
      <IssueBox ref={this.issueToolRef}>
        <div className="tooltip-info">
          {t('Select the part of content you want to comment')}
        </div>
        {emptyMessage && (
          <div className="error">
            <div>
              <FontAwesomeIcon icon={faExclamationTriangle} />
            </div>
            <div>
              <span>{t('Please add your comment')}</span>
            </div>
          </div>
        )}
        <TextareaAutosize
          className="issue-message"
          value={message}
          onChange={this.onChangeMessage}
          name="issueMsg"
          placeholder={t('Enter a comment')}
          autoFocus
        />

        <div className="actions">
          <button className="primary" onClick={this.onAddIssue}>
            {t('Add')}
          </button>
          <button className="secondary" onClick={this.onCloseHandler}>
            {t('Cancel')}
          </button>
        </div>
      </IssueBox>
    );
  }
}

export default withTranslation()(IssueTool);
